import {
    Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun,
    WidthType, AlignmentType
  } from "docx";
  import { saveAs } from "file-saver";
  import {
    COVER_LETTER, LEGAL_INTRO, OBJECTIVES, LIMITATIONS,
    buildGeneralInfoTable, buildOccupantsTable, buildFireLossTable, buildLegislationTable,
    RISK_DEFINITIONS, RISK_MATRIX, FIRE_HAZARDS, FIRE_PROTECTION, PROCEDURES,
    buildActionPlanTable, buildReviewTable, SIGNATURES
  } from "./reportStructure";
  
  // Helper for section headings
  const sectionHeading = (text) =>
    new Paragraph({
      text,
      heading: "HEADING_2",
      spacing: { after: 180, before: 120 },
      thematicBreak: true
    });
  
  const subHeading = (text) =>
    new Paragraph({
      text,
      heading: "HEADING_3",
      spacing: { after: 50, before: 60 }
    });
  
  // Helper for simple bold label
  const boldPara = (text) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true })],
      spacing: { after: 50 }
    });
  
  // Helper for bullet
  const bullet = (text) =>
    new Paragraph({
      text,
      bullet: { level: 0 },
      spacing: { after: 30 }
    });
  
  export const generateFireRiskDocx = async (data = {}, questions = {}) => {
    const children = [
      // Cover
      new Paragraph({
        text: "FIRE RISK ASSESSMENT REPORT",
        heading: "TITLE",
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      }),
      ...[
        ["Prepared for", data.auditPreparedFor],
        ["Site address", data.siteAddress],
        ["Assessment date", data.dateConducted],
        ["Assessor", data.auditCompletedBy],
        ["Client contact", data.clientContact]
      ].map(
        ([label, value]) =>
          new Paragraph({
            text: `${label}: ${value || ""}`,
            alignment: AlignmentType.CENTER
          })
      ),
      new Paragraph({ text: "" }),
      // Separation line
      new Paragraph({
        children: [new TextRun({ text: "—", size: 16, color: "00b08a" })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 }
      }),
      // Narrative
      new Paragraph({ text: "Dear Client,", spacing: { after: 30 } }),
      new Paragraph({ text: COVER_LETTER, spacing: { after: 80 } }),
      boldPara("REGULATORY REFORM (FIRE SAFETY) ORDER 2005"),
      new Paragraph({ text: LEGAL_INTRO, spacing: { after: 80 } }),
      boldPara("Objectives:"),
      bullet("To identify and evaluate the fire risks to people and property from fire."),
      bullet("To evaluate the effectiveness of existing fire precautions."),
      bullet("To identify deficiencies and recommend appropriate control measures."),
      boldPara("Limitations:"),
      bullet("This assessment is non-destructive and based on visual inspection at the time."),
      bullet("Areas inaccessible during the assessment are excluded."),
      bullet("No testing of electrical or fire systems was undertaken."),
      // New page for rest of content
      new Paragraph({ pageBreakBefore: true }),
  
      // --- GENERAL INFORMATION ---
      sectionHeading("GENERAL INFORMATION"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: buildGeneralInfoTable(data).map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      }),
  
      // --- OCCUPANTS TABLE ---
      sectionHeading("OCCUPANTS"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph("Type")], width: { size: 40, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph("Present")], width: { size: 15, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph("Comments")], width: { size: 45, type: WidthType.PERCENTAGE } })
            ]
          }),
          ...buildOccupantsTable(data).map(row =>
            new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
          )
        ]
      }),
  
      // --- FIRE LOSS EXPERIENCE TABLE ---
      sectionHeading("FIRE LOSS EXPERIENCE"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({ children: ["Date", "Nature", "Consequences"].map(t => new TableCell({ children: [new Paragraph(t)] })) }),
          ...buildFireLossTable(data).map(row =>
            new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
          )
        ]
      }),
  
      // --- LEGISLATION TABLE ---
      sectionHeading("RELEVANT UK/EU LEGISLATION"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: buildLegislationTable().map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      }),
  
      // --- FIRE HAZARDS & CONTROL MEASURES ---
      sectionHeading("FIRE HAZARDS & CONTROL MEASURES"),
      ...FIRE_HAZARDS.map(mainQ => [
        subHeading(mainQ.main),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Sub-Question")] }),
                new TableCell({ children: [new Paragraph("Yes")] }),
                new TableCell({ children: [new Paragraph("No")] }),
                new TableCell({ children: [new Paragraph("N/A")] }),
                new TableCell({ children: [new Paragraph("Comments")] })
              ]
            }),
            ...mainQ.subs.map(sub =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph(sub.text)] }),
                  new TableCell({ children: [new Paragraph(data.fireHazards?.[sub.key]?.answer === "Yes" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireHazards?.[sub.key]?.answer === "No" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireHazards?.[sub.key]?.answer === "N/A" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireHazards?.[sub.key]?.comments || "")] })
                ]
              })
            )
          ]
        })
      ]).flat(),
  
      // --- FIRE PROTECTION CHECKLIST ---
      sectionHeading("FIRE PROTECTION"),
      ...FIRE_PROTECTION.map(mainQ => [
        subHeading(mainQ.main),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Sub-Question")] }),
                new TableCell({ children: [new Paragraph("Yes")] }),
                new TableCell({ children: [new Paragraph("No")] }),
                new TableCell({ children: [new Paragraph("N/A")] }),
                new TableCell({ children: [new Paragraph("Comments")] })
              ]
            }),
            ...mainQ.subs.map(sub =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph(sub.text)] }),
                  new TableCell({ children: [new Paragraph(data.fireProtection?.[sub.key]?.answer === "Yes" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireProtection?.[sub.key]?.answer === "No" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireProtection?.[sub.key]?.answer === "N/A" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.fireProtection?.[sub.key]?.comments || "")] })
                ]
              })
            )
          ]
        })
      ]).flat(),
  
      // --- PROCEDURES & MANAGEMENT CHECKLIST ---
      sectionHeading("PROCEDURES & MANAGEMENT"),
      ...PROCEDURES.map(mainQ => [
        subHeading(mainQ.main),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Sub-Question")] }),
                new TableCell({ children: [new Paragraph("Yes")] }),
                new TableCell({ children: [new Paragraph("No")] }),
                new TableCell({ children: [new Paragraph("N/A")] }),
                new TableCell({ children: [new Paragraph("Comments")] })
              ]
            }),
            ...mainQ.subs.map(sub =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph(sub.text)] }),
                  new TableCell({ children: [new Paragraph(data.procedures?.[sub.key]?.answer === "Yes" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.procedures?.[sub.key]?.answer === "No" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.procedures?.[sub.key]?.answer === "N/A" ? "✔" : "")] }),
                  new TableCell({ children: [new Paragraph(data.procedures?.[sub.key]?.comments || "")] })
                ]
              })
            )
          ]
        })
      ]).flat(),
  
      // --- RISK EVALUATION DEFINITIONS ---
      sectionHeading("RISK EVALUATION DEFINITIONS"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: RISK_DEFINITIONS.map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      }),
  
      // --- RISK MATRIX TABLE ---
      sectionHeading("RISK MATRIX"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: RISK_MATRIX.map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      }),
  
      // --- ACTION PLAN TABLE ---
      sectionHeading("ACTION PLAN"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: ["Issue", "Justification", "Current Measures", "Additional Controls", "Responsible", "Deadline"]
              .map(h => new TableCell({ children: [new Paragraph(h)] }))
          }),
          ...buildActionPlanTable(data).map(row =>
            new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
          )
        ]
      }),
  
      // --- REVIEW TABLE ---
      sectionHeading("REVIEW"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: buildReviewTable(data).map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      }),
  
      // --- SIGNATURES TABLE ---
      sectionHeading("SIGNATURES"),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: SIGNATURES.map(row =>
          new TableRow({ children: row.map(cell => new TableCell({ children: [new Paragraph(cell)] })) })
        )
      })
    ];
  
    const doc = new Document({
      sections: [{ children }]
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Fire_Risk_Assessment_Report.docx");
  };
  